import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Badge } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { MetricCard } from '../../components/ui/Card.jsx';
import { Table } from '../../components/ui/Table.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { eventLabel } from '../../utils/labels.js';
import { automationApi } from '../../services/automationApi.js';

import { RuleCreateModal } from './RuleCreateModal.jsx';

const SEVERITY_VARIANT = { INFO: 'info', WARNING: 'warning', CRITICAL: 'danger' };
const OP_LABEL = { lt: '<', lte: '≤', gt: '>', gte: '≥', eq: '=', neq: '≠', contains: '⊇', exists: '∃' };

export function RulesPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);

  const rules = useQuery({ queryKey: ['automation', 'rules'], queryFn: () => automationApi.listRules().then((r) => r.items) });
  const runs = useQuery({ queryKey: ['automation', 'runs'], queryFn: () => automationApi.listRuns({ limit: 1 }).then((r) => r.meta) });
  const rows = rules.data || [];

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['automation', 'rules'] });
  const toggle = useMutation({ mutationFn: ({ id, enabled }) => automationApi.setRuleEnabled(id, enabled), onSuccess: invalidate });
  const del = useMutation({ mutationFn: (id) => automationApi.deleteRule(id), onSuccess: invalidate });

  const columns = [
    { key: 'name', label: t('auto.col.name'), render: (r) => <strong>{r.name}</strong> },
    { key: 'trigger', label: t('auto.col.trigger'), render: (r) => <span className="text-sm">{eventLabel(t, r.triggerEvent)}</span> },
    { key: 'condition', label: t('auto.col.condition'), render: (r) => (r.conditionField ? <span className="mono text-sm">{r.conditionField} {OP_LABEL[r.conditionOp] || r.conditionOp} {r.conditionValue}</span> : <span className="muted">{t('auto.always')}</span>) },
    { key: 'severity', label: t('auto.col.severity'), render: (r) => <Badge variant={SEVERITY_VARIANT[r.severity]}>{t(`auto.severity.${r.severity}`)}</Badge> },
    { key: 'runCount', label: t('auto.col.fired'), align: 'right', render: (r) => <span className="mono num">{r.runCount}</span> },
    { key: 'enabled', label: t('auto.col.enabled'), render: (r) => (
      <Button size="sm" variant={r.enabled ? 'secondary' : 'ghost'} disabled={toggle.isPending} onClick={() => toggle.mutate({ id: r.id, enabled: !r.enabled })}>
        {r.enabled ? t('auto.on') : t('auto.off')}
      </Button>
    ) },
    { key: 'actions', label: '', align: 'right', render: (r) => <Button size="sm" variant="ghost" disabled={del.isPending} onClick={() => del.mutate(r.id)}>{t('auto.action.delete')}</Button> },
  ];

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('auto.title')}</h1>
          <p className="muted">{t('auto.subtitle')}</p>
        </div>
        <div className="page-actions">
          <Button variant="primary" onClick={() => setCreateOpen(true)}>{t('auto.action.new')}</Button>
        </div>
      </header>

      <div className="card-grid">
        <MetricCard label={t('auto.metric.rules')} value={String(rows.length)} />
        <MetricCard label={t('auto.metric.active')} value={String(rows.filter((r) => r.enabled).length)} trendVariant="success" />
        <MetricCard label={t('auto.metric.alerts')} value={String(runs.data?.unread ?? 0)} trendVariant={runs.data?.unread ? 'warning' : 'success'} />
      </div>

      <Table columns={columns} rows={rows} isLoading={rules.isLoading} emptyMessage={t('auto.empty')} />

      <RuleCreateModal open={createOpen} onClose={() => setCreateOpen(false)} />
    </section>
  );
}
